import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Alert, CircularProgress, Dialog, DialogContent, Paper } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import StripeWrapper from '../../components/subscription/StripeWrapper';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import SubscriptionFormDirect from '../../components/subscription/SubscriptionFormDirect';

// Make sure to use the publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

interface Plan {
  id: string;
  name: string;
  price: number;
}

// Available subscription plans
const plans: Record<string, Plan> = {
  pro: {
    id: 'pro_monthly',
    name: 'Pro',
    price: 9.99
  }
};

export default function SubscriptionPage() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user.user);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDirectForm, setShowDirectForm] = useState(true);
  
  useEffect(() => {
    // Redirect to login if user is not authenticated
    if (!user) {
      navigate('/login', { state: { returnTo: `/subscription/${planId}` } });
      return;
    }
    
    // Check if the user already has an active subscription
    if (user.hasActiveSubscription) {
      setError('You already have an active subscription.');
      setLoading(false);
      return;
    }
    
    // Validate plan ID
    if (!planId || !plans[planId]) {
      setError('Invalid subscription plan. Please select a valid plan.');
      setLoading(false);
      return;
    }
    
    setLoading(false);
  }, [user, planId, navigate]);
  
  const handleSuccess = () => {
    navigate('/dashboard');
  };
  
  const handleCancel = () => {
    setShowDirectForm(false);
    navigate('/dashboard');
  };
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body1">
            Return to <a href="/pricing">pricing page</a> to select a different plan.
          </Typography>
        </Box>
      </Container>
    );
  }
  
  const selectedPlan = plans[planId!];
  
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Subscribe to {selectedPlan.name} Plan
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Unleash the full power of Tranzcribe with unlimited transcriptions
        </Typography>
      </Box>
      
      {/* Mostrar el formulario directo con un cuadro de diálogo si se viene del botón Upgrade to Pro */}
      <Dialog 
        open={showDirectForm} 
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            bgcolor: '#1a1a2e',
            color: '#fff',
            borderRadius: '12px',
            p: 2
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Elements stripe={stripePromise}>
            <SubscriptionFormDirect 
              onCancel={handleCancel}
              onSuccess={handleSuccess}
              planId={selectedPlan.id}
              planPrice={selectedPlan.price}
            />
          </Elements>
        </DialogContent>
      </Dialog>
      
      {/* Mostrar el formulario tradicional al no usar el modal */}
      {!showDirectForm && (
        <StripeWrapper
          planId={selectedPlan.id}
          planName={selectedPlan.name}
          planPrice={selectedPlan.price}
        />
      )}
    </Container>
  );
} 